import * as queryString from 'query-string';

import { CORVA_API_URLS } from '../constants';
import { attachHTTPHeaders, getAppKeyAndId, getAuthorizationHeaders } from '../utils';

import { AuthRecovery } from './AuthRecovery';
import { DISPLAY_HTTP_MESSAGES } from './httpMessages';

import { apiRequestsRateLimiter } from '~/clients/api/rateLimiter';

const authRecovery = AuthRecovery.getInstance();

const HTTP_ERROR_MESSAGES = {
  400: 'Request Failed',
  403: 'No Permission',
  404: 'Not Found',
};

const TOS_ERROR_MESSAGE = 'Please accept the latest terms of service';
const LOCKED_ERROR_MESSAGE = 'Account has been locked';
const INVALID_VERIFICATION_CODE_MESSAGE = 'Invalid verification code';
const MISSING_VERIFICATION_CODE_MESSAGE = 'Missing verification code';

const HTTP_METHODS_TO_SHOW_ERROR_NOTIFICATIONS = [
  // NOTE: Do not show error notifications for GET method. Because users may request a deleted item
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
];

export class APIException {
  constructor(response, json) {
    this.status = response.status;
    this.statusText = response.statusText;
    this.message = json.message || 'No Message';
    this.errorBody = json;
  }

  isAuthenticationProblem() {
    return this.status === 401;
  }

  isTermsProblem() {
    // FIXME: this.message === TOS_ERROR_MESSAGE is a hack. Back-end team should come
    // with another solution.
    return this.status === 401 && this.message === TOS_ERROR_MESSAGE;
  }

  isLoginProblem() {
    return this.status === 401 && this.errorBody?.errors?.login;
  }

  isLockedAccountProblem() {
    return this.status === 401 && this.errorBody?.errors?.login?.includes(LOCKED_ERROR_MESSAGE);
  }

  isInvalidVerificationCode() {
    return (
      this.status === 401 &&
      this.errorBody?.errors?.login?.includes(INVALID_VERIFICATION_CODE_MESSAGE)
    );
  }

  isMissingVerificationCode() {
    return (
      this.status === 401 &&
      this.errorBody?.errors?.identity?.includes(MISSING_VERIFICATION_CODE_MESSAGE)
    );
  }
}

export const baseUrl = CORVA_API_URLS.API;

const AUTH_HEADERS = getAuthorizationHeaders();
const JSON_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  ...AUTH_HEADERS,
};

export function handleAuthenticationProblem(e) {
  authRecovery.handleAuthFailure(e);
}

const V1_ASSETS_REGEXP = /^\/v1\/assets.*/;

async function requestCore(path, config = {}, overrides = {}) {
  const { apiUrl = baseUrl, appKey, appInstanceId } = overrides;

  if (appKey && appInstanceId && !overrides.isRetry) {
    // Skip rate-limiter acquisition on retries. When a 401 triggers a token
    // refresh, errorHandler re-invokes requestCore with isRetry: true.
    // The original call already consumed a rate-limiter token, so acquiring
    // again would double-count a single logical request.
    // Because in the RateLimiter there is no release(),
    // the extra acquire on retries is an unrecoverable leak.
    await apiRequestsRateLimiter.acquire(appKey, appInstanceId);
  }

  // temporary enable cache for the heaviest request
  if (V1_ASSETS_REGEXP.test(path)) {
    config.cache = 'reload'; // eslint-disable-line no-param-reassign
  }

  config.credentials = 'include'; // eslint-disable-line no-param-reassign

  return fetch(`${apiUrl}${path}`, attachHTTPHeaders({ config, appKey, ...AUTH_HEADERS }));
}

/**
 * Used as refreshFn for AuthRecovery.
 * Triggers a token refresh request using the /v1/user_token endpoint.
 * The refresh mechanism is based on the refresh token stored in the dedicated HTTP-only cookie.
 *
 * Should not be used outside of AuthRecovery and Corva UI.
 */
export function refreshToken() {
  return requestCore('/v1/user_token', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ refresh_token: true }),
  }).then(response => {
    if (!response.ok) {
      throw new APIException(response, {});
    }
  });
}

function errorHandlerDecorator(requestCoreFunc) {
  return async function errorHandler(path, config, overrides = {}) {
    const response = await requestCoreFunc(path, config, overrides);

    const { status } = response;

    if (response.ok) {
      return status === 204 // NOTE: No content
        ? null
        : response;
    }

    // NOTE: Handle Error response logic
    let json;
    try {
      json = await response.json();
    } catch (e) {
      json = {};
    }
    const exception = new APIException(response, json);

    // Token expired: attempt refresh and retry the original request once.
    // Skip non-refreshable 401s (Terms of Service, login-credential errors) and the login/token
    // endpoint itself, where a refresh would be either pointless or self-recursive.
    const isLoginEndpoint = path.startsWith('/v1/user_token');
    if (authRecovery.isRefreshableAuthError(exception) && !isLoginEndpoint && !overrides.isRetry) {
      try {
        await authRecovery.tryRefresh();
        return errorHandler(path, config, { ...overrides, isRetry: true }); // Ensure retrying only once.
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Refresh failed — fall through to authRecovery.handleAuthFailure
      }
    }

    const requestMethod = config.method;
    const isMethodToShowNotification =
      !overrides.hideNotification &&
      HTTP_METHODS_TO_SHOW_ERROR_NOTIFICATIONS.includes(requestMethod);

    const httpErrorMessage = DISPLAY_HTTP_MESSAGES[json.message] || HTTP_ERROR_MESSAGES[status];

    if (isMethodToShowNotification && httpErrorMessage) {
      console.error(httpErrorMessage);
    }

    authRecovery.handleAuthFailure(exception);

    throw exception;
  };
}

const request = errorHandlerDecorator(requestCore);

/**
 * The post method is most-often utilized to **create** new resources.
 * @async
 * @param {string} path - path to resource.
 * @param {Object=} entity - API entity to create
 * @returns {Promise<*>}
 */
export async function post(path, entity, requestOptions = {}) {
  const { apiUrl, withHeaders = false } = requestOptions;
  const response = await request(
    path,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(entity),
    },
    { apiUrl, ...getAppKeyAndId() }
  );

  if (withHeaders) return response;

  return response && response.json();
}

export async function getWithHeaders(path, queryParams = {}, requestOptions = {}) {
  const { apiUrl, signal } = requestOptions;
  const qry = queryString.stringify(queryParams, { arrayFormat: 'bracket' });
  const { appKey, ...rest } = getAppKeyAndId() || {};
  const response = await request(
    `${path}${qry ? '?' : ''}${qry}`,
    {
      method: 'GET',
      signal,
    },
    { apiUrl, appKey: requestOptions.appKey || appKey, ...rest }
  );

  return {
    data: response && (await response.json()),
    headers: response && response.headers,
  };
}

/**
 * The get method is used to **read** (or retrieve) a representation of a resource
 * @async
 * @param {string} path - path to resource.
 * @param {Object=} queryParams - Object with query params such as per_page, sort, asset_id, etc.
 * @param {Object=} requestOptions - Additional options for request. For example: apiUrl, signal etc.
 * @returns {Promise<*>}
 */
export async function get(path, queryParams = {}, requestOptions = {}) {
  const { appKey, ...rest } = getAppKeyAndId() || {};
  const dataAndHeaders = await getWithHeaders(path, queryParams, {
    ...requestOptions,
    appKey: requestOptions.appKey || appKey,
    ...rest,
  });
  return dataAndHeaders.data;
}

export async function getFile(path, queryParams = {}, requestOptions = {}) {
  const qry = queryString.stringify(queryParams);
  const { signal } = requestOptions;
  const response = await request(
    `${path}${qry ? '?' : ''}${qry}`,
    { method: 'GET' },
    { ...getAppKeyAndId(), signal }
  );

  return response && response.blob();
}

export async function put(path, content, queryParams = {}, requestOptions = {}) {
  const { apiUrl, signal } = requestOptions;
  const qry = queryString.stringify(queryParams);
  const response = await request(
    `${path}${qry ? '?' : ''}${qry}`,
    {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify(content),
    },
    { apiUrl, ...getAppKeyAndId(), signal }
  );

  return response && response.json();
}

export async function patch(path, content, queryParams = {}, requestOptions = {}) {
  const { apiUrl, signal } = requestOptions;
  const qry = queryString.stringify(queryParams);

  const response = await request(
    `${path}${qry ? '?' : ''}${qry}`,
    {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(content),
    },
    {
      apiUrl,
      ...getAppKeyAndId(),
      signal,
    }
  );

  return response && response.json();
}

export async function del(path, queryParams = {}, requestOptions = {}) {
  const { apiUrl, signal } = requestOptions;
  const query = queryString.stringify(queryParams);
  const response = await request(
    query ? `${path}?${query}` : path,
    {
      method: 'DELETE',
      headers: JSON_HEADERS,
    },
    {
      apiUrl,
      ...getAppKeyAndId(),
      signal,
    }
  );

  return response && response.json();
}

export async function sendFormData(path, data, queryParams = {}, requestOptions = {}) {
  const query = queryString.stringify(queryParams);
  const { method = 'POST', signal } = requestOptions;
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(arrayValueEntity => {
        formData.append(key, arrayValueEntity);
      });
    } else {
      formData.append(key, value);
    }
  });

  const response = await request(
    query ? `${path}?${query}` : path,
    {
      method,
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    },
    { ...getAppKeyAndId(), signal }
  );

  return response && response.json();
}
